import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import {
  selectSelectedBooking,
  setSelectedBooking,
} from '@store/accommodation';
import { BookingStatusBadge } from './BookingStatusBadge';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function computeNights(checkIn: string, checkOut: string): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round(
    (new Date(checkOut).getTime() - new Date(checkIn).getTime()) / msPerDay,
  );
}

interface DetailRowProps {
  label: string;
  children: React.ReactNode;
}

function DetailRow({ label, children }: DetailRowProps) {
  return (
    <>
      <dt className="text-xs text-gray-400 font-medium">{label}</dt>
      <dd className="text-xs text-gray-800 font-semibold text-right">
        {children}
      </dd>
    </>
  );
}

interface BookingPreviewPanelProps {
  className?: string;
}

export const BookingPreviewPanel: React.FC<BookingPreviewPanelProps> = ({
  className = '',
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const selectedBooking = useAppSelector(selectSelectedBooking);

  const isOpen = selectedBooking !== null;

  const handleClose = () => {
    dispatch(setSelectedBooking(null));
  };

  const handleViewFullDetails = () => {
    if (!selectedBooking) return;
    navigate(`/accommodation/bookings/${selectedBooking.id}`);
    dispatch(setSelectedBooking(null));
  };

  // Derive display values (safe to compute even when null for type-narrowing below)
  const booking = selectedBooking;
  const client = booking?.client;
  const guestName = client
    ? `${client.first_name} ${client.last_name}`.trim() || 'Unknown Guest'
    : 'Unknown Guest';
  const bookingRef = booking ? `#${booking.id.slice(0, 8).toUpperCase()}` : '';
  const nights = booking
    ? computeNights(booking.check_in, booking.check_out)
    : 0;
  const guestsLabel = booking
    ? `${booking.adults} adult${booking.adults !== 1 ? 's' : ''}${
        booking.children > 0 ? `, ${booking.children} children` : ''
      }`
    : '';
  const roomNumbers = booking?.booking_rooms
    ? booking.booking_rooms
        .map((br) => br.room?.room_number ?? '')
        .filter(Boolean)
        .join(', ') || '—'
    : '—';
  const roomTypeName =
    booking?.booking_rooms?.[0]?.room?.room_type?.name ?? '—';
  const guestCountry =
    booking?.guest_country?.name ?? booking?.guest_country_name ?? '—';
  const sourceName =
    booking?.booking_source?.name ??
    booking?.source_name ??
    booking?.source ??
    '—';

  return (
    <div
      className={`fixed top-0 right-0 h-screen w-80 flex flex-col overflow-hidden bg-white shadow-xl z-50 transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } ${className}`}
    >
      {/* Close button */}
      <button
        type="button"
        onClick={handleClose}
        className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors z-10"
        aria-label="Close preview"
      >
        <X size={16} />
      </button>

      {booking && (
        <>
          {/* Header */}
          <div className="bg-gray-50 p-4 pr-10 flex-shrink-0">
            <h2 className="text-base font-bold text-gray-900 leading-tight">
              {guestName}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">{bookingRef}</p>
            {booking.booking_source && (
              <span className="inline-block mt-2 rounded-full bg-pos-primary/10 text-pos-primary text-xs font-medium px-2.5 py-0.5">
                {booking.booking_source.name}
              </span>
            )}
          </div>

          {/* Details grid */}
          <div className="flex-1 overflow-y-auto p-4">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <DetailRow label="Check in">
                {formatDate(booking.check_in)}
              </DetailRow>
              <DetailRow label="Check out">
                {formatDate(booking.check_out)}
              </DetailRow>
              <DetailRow label="Nights">{nights}</DetailRow>
              <DetailRow label="Guests">{guestsLabel}</DetailRow>
              <DetailRow label="Rooms">{roomNumbers}</DetailRow>
              <DetailRow label="Room type">{roomTypeName}</DetailRow>
              <DetailRow label="Amount">
                {booking.currency} {booking.total_amount.toLocaleString()}
              </DetailRow>
              <DetailRow label="Status">
                <BookingStatusBadge status={booking.status} />
              </DetailRow>
              <DetailRow label="Guest country">{guestCountry}</DetailRow>
              <DetailRow label="Source">{sourceName}</DetailRow>
            </dl>

            {/* Notes */}
            {booking.notes && (
              <div className="mt-4 px-0 pb-1">
                <p className="text-xs text-gray-400 font-medium mb-1">
                  Guest comment/request
                </p>
                <div className="rounded-lg bg-gray-50 p-3">
                  <p className="text-xs text-gray-700 leading-relaxed">
                    {booking.notes}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action button */}
          <div className="p-4 border-t flex-shrink-0">
            <button
              type="button"
              onClick={handleViewFullDetails}
              className="w-full bg-pos-primary text-white rounded-xl py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              View full details
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default BookingPreviewPanel;
